import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignupPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import AuthLayout from "../layout/AuthLayout.jsx";
import { InlineMessage, errorText } from "../ui/Feedback.jsx";

const PROVIDER_LABEL = { google: "Google", github: "GitHub" };

export default function SignupScreen() {
  const api = useApiClient();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [siteKey, setSiteKey] = useState("");

  // What this deployment offers the signup screen. A failure is not fatal: with
  // no answer the widget is simply not rendered, and the server is the one that
  // decides whether a token was required.
  useEffect(() => {
    let active = true;
    api.auth
      .config()
      .then((data) => {
        if (active) setSiteKey(data?.turnstile_site_key ?? "");
      })
      .catch(() => {
        if (active) setSiteKey("");
      });
    return () => {
      active = false;
    };
  }, [api]);

  async function handleSubmit({ email, password, turnstileToken }) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // 201 always means "verify your address next".
      const data = await api.auth.signup({ email, password, turnstile_token: turnstileToken });
      navigate("/2fa", {
        state: {
          challengeId: data?.challenge_id,
          destination: data?.destination,
          purpose: data?.purpose ?? "verify_email",
          email,
          notice: data?.notice ?? null,
        },
      });
    } catch (err) {
      if (err?.code === "forbidden") {
        // The human check is the one 403 this screen can produce, and it is
        // actionable: reloading gets a fresh widget.
        setError("The human check could not be verified. Reload the page and try again.");
        return;
      }
      if (err?.code === "conflict") {
        setError("That email address is already registered.");
      } else {
        setError(errorText(err, "Could not create your account."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSocialLogin(provider) {
    setError(null);
    setNotice(null);
    try {
      const result = await api.auth.startOAuth(provider);
      if (result?.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      if (result?.data?.challenge_required) {
        navigate("/2fa", {
          state: {
            challengeId: result.data.challenge_id,
            destination: result.data.destination,
          },
        });
      }
    } catch (err) {
      if (err?.isNotConfigured) {
        setNotice(
          err.message ||
            `${PROVIDER_LABEL[provider] ?? provider} sign-up is not configured on this server yet. Use the email form instead.`,
        );
      } else {
        setError(errorText(err, "Social sign-up failed."));
      }
    }
  }

  return (
    <AuthLayout>
      {notice ? <InlineMessage tone="warning">{notice}</InlineMessage> : null}
      {error ? <InlineMessage tone="danger">{error}</InlineMessage> : null}
      <SignupPage onSubmit={handleSubmit} onSocialLogin={handleSocialLogin} turnstileSiteKey={siteKey} />
      <p style={{ textAlign: "center", fontSize: "var(--text-xs)", color: "var(--text-muted)", marginTop: 14 }}>
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => navigate("/login")}
          style={{ color: "var(--text-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit" }}
        >
          Log in
        </button>
      </p>
    </AuthLayout>
  );
}
